import {
  fetchKpiBranches,
  fetchKpiPhases,
  fetchKpiSeasons,
  fetchPhaseDailyKpis,
} from "./kpiPhaseService";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "";

export const DASHBOARD_BTT_STORE_IDS = [16,20,22,25,31,32,36,37,39,41,42,
  43,46,47,50,51,53,54,55,56,57,58,59,61,62,63,64,65,67,68,69,70,71,72,
  73,75,76,77,80,83,84,105,108,117,118,119,120,122,123,124,127,131,132,
  133,136,137,138,139,140,141,142,144,145,147,148,149,150,156,157,158,
  160,162,163,164,165,999,1000,1001,1003,1009,1011,1012,78,111,90,128,1,3,4,5,7,6868,6969]

export const DASHBOARD_HCM_STORE_IDS = [78,111,90,128,163];
export const DASHBOARD_BONPAS_STORE_IDS = [1,3,4,5,7];
const DASHBOARD_HCM_STORE_ID_SET = new Set(DASHBOARD_HCM_STORE_IDS);
const DASHBOARD_BONPAS_STORE_ID_SET = new Set(DASHBOARD_BONPAS_STORE_IDS);
export const DASHBOARD_DANANG_STORE_IDS = DASHBOARD_BTT_STORE_IDS.filter(
  (storeId) =>
    !DASHBOARD_HCM_STORE_ID_SET.has(storeId) &&
    !DASHBOARD_BONPAS_STORE_ID_SET.has(storeId)
);

const DASHBOARD_LEDGER_STORE_IDS_BY_LOCATION = {
  all: DASHBOARD_BTT_STORE_IDS,
  danang: DASHBOARD_DANANG_STORE_IDS,
  hcm: DASHBOARD_HCM_STORE_IDS,
  bonpas: DASHBOARD_BONPAS_STORE_IDS,
};

const buildApiUrl = (path, params = {}) => {
  const query = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== "") {
      query.set(key, value);
    }
  });

  return `${API_BASE_URL}${path}${query.toString() ? `?${query}` : ""}`;
};

const fetchJson = async (url, options = {}) => {
  const response = await fetch(url, options);
  const result = await response.json().catch(() => null);

  if (!response.ok || result?.success === false) {
    throw new Error(result?.message || `HTTP ${response.status}`);
  }

  return result;
};

const parseLedgerQuantity = (value) => {
  const quantity = Number(value);
  return Number.isFinite(quantity) && quantity >= 0 ? quantity : 0;
};

export const getLedgerSalesBreakdown = (summary = {}) => {
  const hasSeparatedQuantities =
    summary?.SoLuongBanhBan !== undefined ||
    summary?.SoLuongBanhDat !== undefined;

  if (hasSeparatedQuantities) {
    const banhBan = parseLedgerQuantity(summary.SoLuongBanhBan);
    const banhDat = parseLedgerQuantity(summary.SoLuongBanhDat);

    return {
      banhBan,
      banhDat,
      total: banhBan + banhDat,
    };
  }

  const legacyTotal = parseLedgerQuantity(summary?.SoLuongBan);
  return {
    banhBan: legacyTotal,
    banhDat: 0,
    total: legacyTotal,
  };
};

export const getLedgerChartSalesBreakdown = (summary = {}) => {
  const byCakeType = summary?.byCakeType;
  const cakeTypeEntries =
    Array.isArray(byCakeType)
      ? byCakeType
          .filter((item) => item && typeof item === "object")
          .map((item) => [item.MaLBTT, item])
      : byCakeType && typeof byCakeType === "object"
      ? Object.entries(byCakeType)
      : [];

  if (cakeTypeEntries.length === 0) {
    return getLedgerSalesBreakdown(summary);
  }

  return cakeTypeEntries.reduce(
    (total, [cakeType, cakeTypeSummary]) => {
      if (String(cakeType).trim().toUpperCase() === "HOP") return total;

      const breakdown = getLedgerSalesBreakdown(cakeTypeSummary);
      total.banhBan += breakdown.banhBan;
      total.banhDat += breakdown.banhDat;
      total.total += breakdown.total;
      return total;
    },
    { banhBan: 0, banhDat: 0, total: 0 }
  );
};

export const isKpiPreviousSeasonEnabled = () => {
  const configuredValue = process.env.REACT_APP_ENABLE_KPI_PREVIOUS_SEASON;
  if (configuredValue === undefined || configuredValue === "") return true;

  return String(configuredValue).trim().toLowerCase() === "true";
};

export const getLedgerStoreIdsForLocation = (
  selectedLocation,
  storesData = []
) => {
  const fallbackStoreIds =
    DASHBOARD_LEDGER_STORE_IDS_BY_LOCATION[selectedLocation] ||
    DASHBOARD_LEDGER_STORE_IDS_BY_LOCATION.all;
  const bakeryStores = storesData.filter((store) =>
    String(store.TenCuaHang || store.TenCuaHang_Uni || "")
      .toLocaleLowerCase("vi")
      .includes("bakery")
  );

  if (bakeryStores.length === 0) {
    return fallbackStoreIds;
  }

  const allStoreIds = bakeryStores
    .map((store) => Number(store.IDCH))
    .filter((storeId) => Number.isInteger(storeId) && storeId > 0);
  const hcmStoreIdSet = new Set(
    bakeryStores
      .filter((store) => Number(store.LoaiCH) === 3)
      .map((store) => Number(store.IDCH))
  );
  const bonpasStoreIdSet = new Set(DASHBOARD_BONPAS_STORE_IDS);
  let matchedStoreIds =
    selectedLocation === "all"
      ? [...new Set([...fallbackStoreIds, ...allStoreIds])]
      : allStoreIds;

  if (selectedLocation === "hcm") {
    matchedStoreIds = allStoreIds.filter((storeId) => hcmStoreIdSet.has(storeId));
  } else if (selectedLocation === "bonpas") {
    matchedStoreIds = allStoreIds.filter((storeId) =>
      bonpasStoreIdSet.has(storeId)
    );
  } else if (selectedLocation === "danang") {
    matchedStoreIds = allStoreIds.filter(
      (storeId) =>
        !hcmStoreIdSet.has(storeId) && !bonpasStoreIdSet.has(storeId)
    );
  }

  return matchedStoreIds.length > 0 ? matchedStoreIds : fallbackStoreIds;
};

export const fetchDashboardLedgerSummary = ({
  storeIds,
  startDate,
  endDate,
  signal,
}) => {
  const normalizedStoreIds = (Array.isArray(storeIds) ? storeIds : [])
    .map(Number)
    .filter((storeId) => Number.isInteger(storeId) && storeId > 0);

  if (normalizedStoreIds.length === 0) {
    return Promise.reject(
      new Error("Không xác định được MaCH cho biểu đồ KPI theo ngày.")
    );
  }
  if (!startDate || !endDate) {
    return Promise.reject(
      new Error("Thiếu NgayBatDau hoặc NgayKetThuc cho biểu đồ KPI theo ngày.")
    );
  }

  return fetchJson(
    buildApiUrl("/api/dashboard-btt-ledger-summary", {
      MaCH: normalizedStoreIds.join(","),
      NgayBatDau: startDate,
      NgayKetThuc: endDate,
    }),
    { signal }
  );
};

const getResponseRows = (payload) =>
  Array.isArray(payload)
    ? payload
    : Array.isArray(payload?.data)
      ? payload.data
      : [];

export const fetchPreviousSeasonSales = ({
  startDate,
  endDate,
  signal,
}) => {
  if (
    !/^\d{4}-\d{2}-\d{2}$/.test(startDate || "") ||
    !/^\d{4}-\d{2}-\d{2}$/.test(endDate || "") ||
    startDate > endDate
  ) {
    return Promise.reject(
      new Error("Khoảng ngày lấy số lượng mùa trước không hợp lệ.")
    );
  }

  return fetchJson(buildApiUrl("/api/SaleTrungThuRrevYear"), {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      NgayBatDau: startDate,
      NgayKetThuc: endDate,
    }),
    signal,
  });
};

export const buildPreviousSeasonSalesByDate = (payload) => {
  const salesByDate = new Map();

  getResponseRows(payload).forEach((item) => {
    const currentDate = String(item.NgayHienTai || "").slice(0, 10);
    const quantity = Number(item.TongSoLuong);
    if (!/^\d{4}-\d{2}-\d{2}$/.test(currentDate)) return;

    salesByDate.set(
      currentDate,
      (salesByDate.get(currentDate) || 0) +
        (Number.isFinite(quantity) && quantity >= 0 ? quantity : 0)
    );
  });

  return salesByDate;
};

const normalizeBranchIds = (branchIds) =>
  [...new Set((Array.isArray(branchIds) ? branchIds : [])
    .map(Number)
    .filter((branchId) => Number.isInteger(branchId) && branchId > 0))];

const getManagementBranchIds = async (branchIds, signal) => {
  const normalized = normalizeBranchIds(branchIds);
  if (normalized.length > 0) return normalized;

  const response = await fetchKpiBranches({ signal });
  return normalizeBranchIds(
    getResponseRows(response).map((branch) => branch.ChiNhanh)
  );
};

export const fetchSeasonKpiTargets = async ({ signal } = {}) => {
  const branchIds = await getManagementBranchIds([], signal);
  const responses = await Promise.all(
    branchIds.map((branchId) => fetchKpiSeasons(branchId, { signal }))
  );

  return responses.flatMap((response, index) =>
    getResponseRows(response).map((season) => ({
      ...season,
      ChiNhanh: Number(season.ChiNhanh || branchIds[index]),
      NgayKetThuc: season.KPI_NgayKetThuc || season.NgayKetThuc,
    }))
  );
};

export const fetchPhaseKpiSummary = async ({
  branchIds = [],
  startDate,
  endDate,
  signal,
} = {}) => {
  const requestedBranchIds = await getManagementBranchIds(branchIds, signal);
  const branchResults = await Promise.all(
    requestedBranchIds.map(async (branchId) => {
      const seasonResponse = await fetchKpiSeasons(branchId, { signal });
      const seasons = getResponseRows(seasonResponse);
      const phaseResponses = await Promise.all(
        seasons.map((season) =>
          fetchKpiPhases(season.IDMua, branchId, { signal })
        )
      );

      return phaseResponses.flatMap((response, index) => {
        const season = seasons[index];
        return getResponseRows(response)
          .filter((phase) => {
            const phaseStart = String(phase.NgayBatDau || "").slice(0, 10);
            const phaseEnd = String(phase.NgayKetThuc || "").slice(0, 10);
            return (
              phaseStart &&
              phaseEnd &&
              (!startDate || phaseEnd >= startDate) &&
              (!endDate || phaseStart <= endDate)
            );
          })
          .map((phase) => ({
            ...phase,
            IDPhase: phase.ID,
            IDKpi: season.IDKpi,
            SoPhase: phase.Phase,
            TenPhase: `Phase ${phase.Phase}`,
            KPI: phase.KPIChiNhanh ?? phase.KPI ?? phase.KPITong ?? 0,
            TenMuaVu: season.TenMuaVu,
            IDMua: season.IDMua,
            ChiNhanh: branchId,
          }));
      });
    })
  );

  return { success: true, data: branchResults.flat() };
};

export const fetchPhaseDailySummary = async ({
  branchIds = [],
  startDate,
  endDate,
  phaseRows,
  signal,
} = {}) => {
  const phases = Array.isArray(phaseRows)
    ? phaseRows
    : getResponseRows(
        await fetchPhaseKpiSummary({
          branchIds,
          startDate,
          endDate,
          signal,
        })
      );
  const dayResponses = await Promise.all(
    phases.map((phase) =>
      fetchPhaseDailyKpis(phase.IDPhase, phase.ChiNhanh, { signal })
    )
  );
  const grouped = new Map();

  dayResponses.forEach((response, index) => {
    const phase = phases[index];
    if (!grouped.has(phase.ChiNhanh)) {
      grouped.set(phase.ChiNhanh, []);
    }
    const days = Array.isArray(response?.data?.days) ? response.data.days : [];
    grouped.get(phase.ChiNhanh).push(
      ...days.map((day) => ({
        ...day,
        IDPhase: phase.IDPhase,
        ChiNhanh: phase.ChiNhanh,
        NgayBatDauPhase: phase.NgayBatDau,
        NgayKetThucPhase: phase.NgayKetThuc,
      }))
    );
  });

  return {
    success: true,
    data: Array.from(grouped.entries()).map(([ChiNhanh, Data]) => ({
      ChiNhanh,
      Data: Data.sort((first, second) =>
        String(first.Ngay).localeCompare(String(second.Ngay))
      ),
    })),
  };
};

export const fetchDashboardStores = ({ signal } = {}) =>
  fetchJson(buildApiUrl("/stores"), { signal });
